import { Url } from '@rossum/api-client';
import {
  SchemaAction,
  SchemaRule,
  SchemaSection,
} from '@rossum/api-client/schemas';
import { LinearProgress, Stack } from '@rossum/ui/material';
import {
  DataGridPro,
  gridClasses,
  GridPaginationModel,
} from '@rossum/ui/x-data-grid-pro';
import { merge } from 'lodash';
import { ReactNode, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { mapKeys, mapValues, omitBy, pipe } from 'remeda';
import { commonDataGridStyles } from '../../../ui/data-grid/styles';
import { ContentForPreview } from './hooks/makeArtificialContent';
import { useCalculatePreview } from './hooks/useCalculatePreview';
import { useCalculateRulesPreview } from './hooks/useCalculateRulesPreview';
import { useColumns } from './hooks/useColumns';
import { useUpdateColumWidth } from './hooks/useColumnWidths';
import {
  DEFAULT_PAGE_SIZE,
  useGetAnnotationsForPreview,
} from './hooks/useGetAnnotationsForPreview';
import { useQueueAndWorkspaceName } from './hooks/useQueueAndWorkspaceName';
import {
  actionsColumn,
  conditionsColumn,
  documentsColumn,
} from './rows/helpers';
import { useRows } from './rows/useRows';

const PAGE_SIZES = [DEFAULT_PAGE_SIZE, 10, 15, 20];

// Use lodash instead of remeda for performance reasons
const gridStyles = merge(
  { ...commonDataGridStyles },
  {
    [`.${gridClasses.cell}`]: {
      py: 1,
    },
  }
);

export type FormulaPreviewGridProps = {
  queueUrls: Url[];
  currentFormulaId: string;
  schemaForPreview: SchemaSection[];
  schemaRules?: Partial<SchemaRule>[];
  type: 'rule' | 'formula';
  renderAction?: (action: SchemaAction) => ReactNode;
};

export const FormulaPreviewGrid = ({
  queueUrls,
  currentFormulaId,
  schemaForPreview,
  schemaRules,
  type,
  renderAction,
}: FormulaPreviewGridProps) => {
  const intl = useIntl();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: DEFAULT_PAGE_SIZE,
    page: 0,
  });

  const annotationsQuery = useGetAnnotationsForPreview(
    queueUrls,
    paginationModel.pageSize
  );

  const annotationUrls = useMemo(() => {
    return (
      annotationsQuery.data?.pages.flatMap(page =>
        page.results.map(({ url }) => url)
      ) ?? []
    );
  }, [annotationsQuery.data]);

  const { queueName, workspaceName } = useQueueAndWorkspaceName({
    queueUrl: queueUrls[0],
  });

  const [dependencies, setDependencies] = useState<Record<string, string>>({});

  const testContent: ContentForPreview[] = [
    {
      type: 'from_dependencies' as const,
      dependencies,
    },
    ...annotationUrls.map(url => ({
      annotationUrl: url,
      type: 'from_annotation' as const,
    })),
  ];

  const evaluationQueriesFormula = useCalculatePreview({
    content: testContent,
    schemaContent: schemaForPreview,
    disabled: type === 'rule',
    queueName,
    workspaceName,
  });

  const evaluationQueriesRules = useCalculateRulesPreview(
    testContent,
    schemaForPreview,
    type === 'formula',
    schemaRules
  );

  const evaluationQueries =
    type === 'formula' ? evaluationQueriesFormula : evaluationQueriesRules;

  const columns = useColumns({
    currentFormulaId,
    schemaForPreview,
    type,
    renderAction,
    queueName,
  });

  const pageData = useMemo(
    () => annotationsQuery.data?.pages[paginationModel.page],
    [annotationsQuery.data?.pages, paginationModel.page]
  );

  const rows = useRows({
    showPreviewLine: true,
    pageSize: paginationModel.pageSize,
    columns,
    annotations: pageData?.results,
    documents: pageData?.documents,
    evaluationQueriesFormula:
      type === 'formula' ? evaluationQueriesFormula : undefined,
    evaluationQueriesRules:
      type === 'rule' ? evaluationQueriesRules : undefined,
  });

  const paginationNextIconButtonProps = useMemo(
    () => ({
      sx: annotationsQuery.isFetching
        ? {
            color: 'text.disabled',
            pointerEvents: 'none',
          }
        : {},
    }),
    [annotationsQuery.isFetching]
  );

  const updateColumnWidth = useUpdateColumWidth();

  return (
    <Stack sx={{ background: theme => theme.palette.background.paper, mt: 2 }}>
      <DataGridPro
        slots={{
          loadingOverlay: LinearProgress,
        }}
        slotProps={{
          noRowsOverlay: {
            sx: { backgroundColor: 'inherit' },
          },
          loadingOverlay: {
            color: 'secondary',
          },
          pagination: {
            nextIconButtonProps: paginationNextIconButtonProps,
            sx: {
              // hide pagination when there are no rows
              display: rows.length === 0 ? 'none' : 'inherit',
              [`> * p`]: {
                margin: 0,
              },
            },
          },
        }}
        localeText={
          rows.length === 0
            ? {
                noRowsLabel: intl.formatMessage({
                  id: 'features.queueSettings.fields.formulaPreview.noAnnotations',
                }),
              }
            : undefined
        }
        autoHeight
        loading={
          annotationsQuery.isFetching ||
          evaluationQueries.some(query => query.isFetching)
        }
        columns={columns}
        // For mysterious reasons, the header is not 80px high but 50-ish
        // I don't know why, but i account for it here
        columnHeaderHeight={80}
        rows={rows}
        editMode="row"
        isCellEditable={params => params.row.annotationUrl === 'preview'}
        processRowUpdate={newRow => {
          // After editing, collect previews from newRow
          // Then update dependencies and re-evaluate the previews

          const rowModelField = pipe(
            newRow,
            omitBy((_, key) => !key.startsWith('columns.')),
            mapKeys(key => key.replace('columns.', '')),
            mapValues(newRowValue => {
              if (
                typeof newRowValue === 'object' &&
                newRowValue !== null &&
                'preview' in newRowValue
              ) {
                return newRowValue.preview;
              }
              return '';
            })
          );

          setDependencies(rowModelField);
          return newRow;
        }}
        onColumnWidthChange={updateColumnWidth}
        density="compact"
        disableColumnMenu
        disableColumnReorder
        pagination
        paginationModel={paginationModel}
        paginationMode="server"
        getRowHeight={() => 'auto'}
        onPaginationModelChange={newModel => {
          setPaginationModel(newModel);

          if (
            annotationsQuery.data &&
            annotationsQuery.data.pages.length <= newModel.page
          ) {
            annotationsQuery.fetchNextPage();
          }
        }}
        rowCount={annotationsQuery.data?.pages[0]?.pagination.total ?? 0}
        pageSizeOptions={PAGE_SIZES}
        disableRowSelectionOnClick
        initialState={{
          pinnedColumns: {
            left: [documentsColumn, conditionsColumn, actionsColumn],
          },
        }}
        sx={gridStyles}
      />
    </Stack>
  );
};
